import { useMutation, useQueryClient } from '@tanstack/react-query';

import { fbmSubstitutionRulesV1Api } from 'api/fbmSubstitutionRulesV1';
import { ApiError } from 'shared/errors/ApiError';

import { substitutionRuleEventsQueryKeys } from './useSubstitutionRuleEventsQuery';
import { substitutionRulesQueryKeys } from './useSubstitutionRulesQuery';

/**
 * Creates a substitution rule group; on success invalidates the rules list and event log.
 *
 * @param {Omit<
 *   import('@tanstack/react-query').UseMutationOptions<
 *     Object,
 *     ApiError,
 *     {
 *       fbmAccountName: string,
 *       originalExternalSku: number,
 *       substitutes: Array<{ substituteExternalSku: number, substituteQuantity: number }>,
 *     }
 *   >,
 *   'mutationFn'
 * >} [mutationOptions={}]
 * @returns {import('@tanstack/react-query').UseMutationResult<
 *   Object,
 *   ApiError,
 *   {
 *     fbmAccountName: string,
 *     originalExternalSku: number,
 *     substitutes: Array<{ substituteExternalSku: number, substituteQuantity: number }>,
 *   }
 * >}
 */
export function useCreateSubstitutionRuleMutation(mutationOptions = {}) {
    const queryClient = useQueryClient();
    const { onSuccess, ...rest } = mutationOptions;

    return useMutation({
        mutationFn: async ({ fbmAccountName, originalExternalSku, substitutes }) => {
            const response = await fbmSubstitutionRulesV1Api.createRule(fbmAccountName, {
                originalExternalSku,
                substitutes,
            });
            if (!response?.status) {
                throw new ApiError(
                    response?.msg ?? 'Failed to create substitution rule',
                    response?.statusCode,
                    response?.data,
                );
            }
            return response.data;
        },
        onSuccess: (data, variables, ...args) => {
            queryClient.invalidateQueries({
                queryKey: substitutionRulesQueryKeys.account(variables.fbmAccountName),
            });
            queryClient.invalidateQueries({
                queryKey: substitutionRuleEventsQueryKeys.account(variables.fbmAccountName),
            });
            return onSuccess?.(data, variables, ...args);
        },
        ...rest,
    });
}
